import { gql, useMutation } from '@apollo/client';
import Button from '@orn/shared-web/components/Button/Button';
import Form from '@orn/shared-web/components/Form';
import Input from '@orn/shared-web/components/Input';
import Modal from '@orn/shared-web/components/Modal';
import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';

import type {
  UpdateFinancialAccountMutation,
  UpdateFinancialAccountMutationVariables,
} from './__generated__/UpdateAccountModal.gql';

const UPDATE_FINANCIAL_ACCOUNT = gql`
  mutation UpdateFinancialAccount($input: UpdateFinancialAccountInput!) {
    updateFinancialAccount(input: $input) {
      id
      name
    }
  }
`;

const MAX_NAME_LENGTH = 255;

interface IAccount {
  id: string;
  name: string;
}

interface IBaseUpdateAccountModalProps {
  account: IAccount;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSubmit: (data: { name: string }) => void;
  isLoading?: boolean;
}

export const BaseUpdateAccountModal = ({
  account,
  isOpen,
  onOpenChange,
  onSubmit,
  isLoading = false,
}: IBaseUpdateAccountModalProps) => {
  const [name, setName] = React.useState(account.name);

  React.useEffect(() => {
    setName(account.name);
  }, [account]);

  const { isValid, errorMessage } = React.useMemo(() => {
    const trimmedName = name.trim();
    if (trimmedName.length === 0) {
      return {
        isValid: false,
        errorMessage: 'Name cannot be empty',
      };
    }
    if (trimmedName.length > MAX_NAME_LENGTH) {
      return {
        isValid: false,
        errorMessage: 'Name cannot be longer than 255 characters',
      };
    }
    return {
      isValid: true,
      errorMessage: '',
    };
  }, [name]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid) {
      onSubmit({ name: name.trim() });
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setName(account.name);
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} className="w-100">
      <Modal.Header onClose={handleClose}>
        <div className="flex flex-col gap-0.5">
          <div className="text-primary text-2xl font-semibold">Account details</div>
          <div className="text-tertiary text-sm font-normal">
            Updating your account details below will affect how your account is displayed in the app.
          </div>
        </div>
      </Modal.Header>
      <Modal.Body className="flex-col items-start gap-5 px-6 pb-6 pt-0">
        <Form onSubmit={handleSubmit} className="w-full">
          <div className="flex w-full flex-col gap-8">
            <div className="flex w-full">
              <Input label="Name" value={name} onChange={setName} isInvalid={!isValid} hint={errorMessage} />
            </div>
            <div className="flex w-full gap-3">
              <Button color="secondary" onClick={handleClose} isDisabled={isLoading} className="flex-1" size="lg">
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                isLoading={isLoading}
                isDisabled={!isValid || isLoading}
                className="flex-1"
                size="lg"
              >
                Save details
              </Button>
            </div>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

interface IUpdateAccountModalProps {
  account: IAccount;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSubmit?: (data: { name: string }) => void;
}

const UpdateAccountModal = ({ account, isOpen, onOpenChange, onSubmit }: IUpdateAccountModalProps) => {
  const [updateFinancialAccount, { loading }] = useMutation<
    UpdateFinancialAccountMutation,
    UpdateFinancialAccountMutationVariables
  >(UPDATE_FINANCIAL_ACCOUNT);

  const handleSubmit = async (data: { name: string }) => {
    try {
      await updateFinancialAccount({
        variables: {
          input: {
            id: account.id,
            name: data.name,
          },
        },
      });

      onSubmit?.(data);
      onOpenChange(false);
    } catch (error) {
      logger.error('Failed to update financial account:', error);
    }
  };

  return (
    <BaseUpdateAccountModal
      account={account}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      onSubmit={handleSubmit}
      isLoading={loading}
    />
  );
};

export default UpdateAccountModal;
